import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import ListContainer from '../ui/ListContainer'
import { useToast } from '../ui/ToastProvider'

export default function FilesPage() {
    const [files, setFiles] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [shareOpen, setShareOpen] = useState(false)
    const [shareFile, setShareFile] = useState(null)
    const [expiresAt, setExpiresAt] = useState('')
    const [uploading, setUploading] = useState(false)
    const { show } = useToast()

    useEffect(() => {
        let ignore = false
        async function load() {
            setLoading(true)
            setError(null)
            try {
                const res = await api.get('/files')
                const data = res?.data?.data ?? res?.data ?? []
                if (!ignore) setFiles(Array.isArray(data) ? data : [])
            } catch (e) {
                if (!ignore) setError('파일 목록을 불러오지 못했습니다')
            } finally {
                if (!ignore) setLoading(false)
            }
        }
        load()
        return () => { ignore = true }
    }, [])

    const onPreview = async (file) => {
        try {
            const res = await api.get(`/files/${file.id}/preview`, { responseType: 'blob' })
            const url = URL.createObjectURL(res.data)
            window.open(url, '_blank')
            URL.revokeObjectURL(url)
        } catch { }
    }

    const onOpenShare = (file) => {
        setShareFile(file)
        setShareOpen(true)
    }

    const onUpload = async (e) => {
        const f = e.target.files?.[0]
        if (!f) return
        setUploading(true)
        try {
            const form = new FormData()
            form.append('file', f)
            await api.post('/files', form, { headers: { 'Content-Type': 'multipart/form-data' } })
            show('업로드 완료', 'success')
            const res = await api.get('/files')
            const data = res?.data?.data ?? res?.data ?? []
            setFiles(Array.isArray(data) ? data : [])
        } catch (err) {
            show('업로드 실패: ' + (err.response?.data?.message || err.message), 'error')
        } finally {
            setUploading(false)
            e.target.value = ''
        }
    }

    const onDelete = async (file) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return
        try {
            await api.delete(`/files/${file.id}`)
            show('삭제되었습니다', 'success')
            setFiles(prev => prev.filter(x => x.id !== file.id))
        } catch (err) {
            show('삭제 실패: ' + (err.response?.data?.message || err.message), 'error')
        }
    }

    const onCreateShare = async (e) => {
        e.preventDefault()
        try {
            const res = await api.post(`/files/${shareFile.id}/share`, {
                expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null
            })
            const link = res?.data?.data?.url ?? res?.data?.url
            if (link) {
                await navigator.clipboard.writeText(link)
                alert('공유 링크가 복사되었습니다!')
            }
            setShareOpen(false)
            setShareFile(null)
            setExpiresAt('')
        } catch (err) {
            alert('공유 링크 생성 실패: ' + (err.response?.data?.message || err.message))
        }
    }

    const header = null

    const table = (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-4 py-2 text-left">이름</th>
                    <th className="px-4 py-2 text-left">크기</th>
                    <th className="px-4 py-2 text-left">업로드</th>
                    <th className="px-4 py-2"></th>
                </tr>
            </thead>
            <tbody>
                {files.map(f => (
                    <tr key={f.id} className="border-t hover:bg-slate-50">
                        <td className="px-4 py-2">{f.name}</td>
                        <td className="px-4 py-2">{f.sizeLabel || f.size || '-'}</td>
                        <td className="px-4 py-2">{formatDateTimeMinute(f.createdAt)}</td>
                        <td className="px-4 py-2 text-right">
                            <button className="text-brand-navy hover:underline mr-3" onClick={() => onPreview(f)}>미리보기</button>
                            <button className="text-brand-navy hover:underline mr-3" onClick={() => onOpenShare(f)}>공유 링크</button>
                            <button className="text-red-600 hover:underline" onClick={() => onDelete(f)}>삭제</button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    return (
        <section>
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">파일</h1>
                <label className="text-sm">
                    <span className={`rounded border px-3 py-2 inline-block cursor-pointer ${uploading ? 'opacity-60' : ''}`}>{uploading ? '업로드 중…' : '+ 파일 업로드'}</span>
                    <input type="file" className="hidden" onChange={onUpload} disabled={uploading} />
                </label>
            </div>

            <ListContainer
                loading={loading}
                error={error}
                onRetry={async () => {
                    try {
                        const res = await api.get('/files')
                        const data = res?.data?.data ?? res?.data ?? []
                        setFiles(Array.isArray(data) ? data : [])
                    } catch (e) {
                        setError('파일 목록을 불러오지 못했습니다')
                    }
                }}
                header={header}
                table={table}
                footer={null}
            />

            {shareOpen && shareFile && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4">
                    <form onSubmit={onCreateShare} className="w-full max-w-sm rounded bg.white p-6 space-y-4">
                        <h2 className="text-lg font-semibold">공유 링크 생성</h2>
                        <div className="text-sm">파일: <span className="font-medium">{shareFile.name}</span></div>
                        <div>
                            <label className="block text-sm">만료일(선택)</label>
                            <input type="datetime-local" className="mt-1 w-full rounded border px-3 py-2" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                            <p className="text-xs text-slate-500 mt-1">비워두면 만료 없이 생성됩니다</p>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => { setShareOpen(false); setShareFile(null); setExpiresAt('') }}>취소</button>
                            <button type="submit" className="rounded bg-brand-navy text-white px-3 py-2 text-sm">생성</button>
                        </div>
                    </form>
                </div>
            )}
        </section>
    )
}


